import { NgIf, NgFor, NgClass, AsyncPipe, DecimalPipe } from '@angular/common';
import { Component } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { PageToolbarComponent } from '@next/core-lib/components';
import { TranslatePipe, AgrDatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import CycleUtils from 'src/app/cycle/cycle-utils';
import { ObservationsRegistration } from 'src/app/performance/models/performance.model';
import { loadObservationsForCycleRequest } from 'src/app/performance/store/actions/performance.actions';
import { getObservations } from 'src/app/performance/store/selectors/performance.selector';
import Utils from 'src/app/utils';

import { Cycle, Gender } from '../../models/cycle.model';
import * as cycleActions from '../../store/actions/cycle.actions';
import { getCycles } from '../../store/selectors/cycle.selectors';

import { CycleOverviewPanelComponent } from '../cycle-overview-panel/cycle-overview-panel.component';

@Component({
  selector: 'app-cycle-overview',
  templateUrl: './cycle-overview.component.html',
  styleUrls: ['cycle-overview.component.scss'],
  imports: [
    PageToolbarComponent,
    MatToolbar,
    MatToolbarRow,
    NgIf,
    MatLabel,
    MatSelect,
    ReactiveFormsModule,
    FormsModule,
    NgFor,
    MatOption,
    MatButton,
    MatIcon,
    CycleOverviewPanelComponent,
    NgClass,
    AsyncPipe,
    DecimalPipe,
    TranslatePipe,
    AgrDatePipe,
  ],
})
export class CycleOverviewComponent {
  cycles$: Observable<Cycle[]> = this.store.select(getCycles).pipe(
    map((cycles) => {
      cycles = cycles.sort((a, b) => (a.cycleNumber > b.cycleNumber ? 1 : -1));
      this.selectedCycle = cycles[cycles.length - 1];
      if (this.selectedCycle) {
        this.store.dispatch(loadObservationsForCycleRequest({ cycleId: this.selectedCycle.id }));
      }
      return cycles;
    }),
  );

  observations$: Observable<ObservationsRegistration[]> = this.store.select(getObservations);
  observations: ObservationsRegistration[] = [];

  selectedCycle: Cycle;

  constructor(
    private readonly store: Store<RootState>,
    private readonly router: Router,
  ) {
    this.observations$.subscribe((o) => {
      this.observations = o;
      if (this.selectedCycle) {
        this.numberOfAnimalsRows = this.numberOfAnimalsTable();
        this.orderInformationRows = this.orderInformationTable();
      }
    });
  }

  cycleChanged(changeEvent: MatSelectChange) {
    this.store.dispatch(loadObservationsForCycleRequest({ cycleId: changeEvent.value.id }));
  }

  compareCycle = (c1: Cycle, c2: Cycle) => c1 && c2 && c1.cycleNumber === c2.cycleNumber;

  getFirstStartDate = (cycle: Cycle): Date => CycleUtils.getCycleStartDate(cycle);

  getTotalAmount = (): number => this.numberOfAnimalsRows.reduce((acc, row) => acc + row.amount, 0);

  addCycle = () => this.router.navigate(['/cycle', 'add']);

  editCycle = () => this.router.navigate(['/cycle', this.selectedCycle.id]);

  deleteCycle() {
    if (this.selectedCycle) {
      this.store.dispatch(
        cycleActions.deleteCycleWithConfirmation({
          cycle: this.selectedCycle,
        }),
      );
    }
  }

  numberOfAnimalsRows: {
    barn: string;
    section: string;
    birthdate: string;
    breed: string;
    gender: string;
    startdate: string;
    amount: number;
  }[] = [];

  numberOfAnimalsTable() {
    const tableRows: {
      barn: string;
      section: string;
      birthdate: string;
      breed: string;
      gender: string;
      startdate: string;
      amount: number;
    }[] = [];

    this.selectedCycle.arrivals.forEach((arrival) => {
      arrival.allocations.forEach((allocation) => {
        if (
          tableRows.findIndex(
            (row) =>
              row.barn === allocation.section.barn.name &&
              row.section === allocation.section.name &&
              row.breed === arrival.breed.name &&
              row.gender === arrival.gender,
          ) === -1
        ) {
          tableRows.push({
            barn: allocation.section.barn.name,
            section: allocation.section.name,
            birthdate: arrival.chickBirthDate,
            breed: arrival.breed.name,
            gender: arrival.gender,
            startdate: arrival.arrivalDate,
            amount: CycleUtils.getNumberOfAnimalsInSectionAtDate(
              this.selectedCycle,
              allocation.section.id,
              Utils.toUtcDate(new Date()),
              this.observations,
              true,
              null,
            ),
          });
        }
      });
    });

    this.selectedCycle.movements.forEach((movement) => {
      if (
        tableRows.findIndex(
          (row) =>
            row.barn === movement.toSection.barn.name &&
            row.section === movement.toSection.name &&
            row.breed === movement.toSection.breed.name &&
            row.gender === movement.toSection.gender,
        ) === -1
      ) {
        tableRows.push({
          barn: movement.toSection.barn.name,
          section: movement.toSection.name,
          birthdate: this.selectedCycle.arrivals.find(
            (arrival) =>
              arrival.allocations.find(
                (allocation) => allocation.section.id === movement.fromSection.id,
              ) !== null,
          ).chickBirthDate,
          breed: movement.toSection.breed.name,
          gender: movement.toSection.gender,
          startdate: movement.movementDate,
          amount: CycleUtils.getNumberOfAnimalsInSectionAtDate(
            this.selectedCycle,
            movement.toSection.id,
            Utils.toUtcDate(new Date()),
            this.observations,
            true,
            null,
          ),
        });
      }
    });
    this.selectedCycle.departures.forEach((departure) => {
      departure.allocations.forEach((allocation) => {
        if (
          tableRows.findIndex(
            (row) =>
              row.barn === allocation.section.barn.name &&
              row.section === allocation.section.name &&
              row.breed === departure.breed.name &&
              row.gender === departure.gender,
          ) === -1
        ) {
          tableRows.push({
            barn: allocation.section.barn.name,
            section: allocation.section.name,
            birthdate: this.selectedCycle.arrivals.find(
              (arrival) =>
                arrival.allocations.find(
                  (allocation) => allocation.section.id === allocation.section.id,
                ) !== null,
            ).chickBirthDate,
            breed: departure.breed.name,
            gender: departure.gender,
            startdate: departure.departureDate,
            amount: CycleUtils.getNumberOfAnimalsInSectionAtDate(
              this.selectedCycle,
              allocation.section.id,
              Utils.toUtcDate(new Date()),
              this.observations,
              true,
              null,
            ),
          });
        }
      });
    });

    tableRows.sort((a, b) => (a.barn.toLowerCase() < b.barn.toLowerCase() ? -1 : 0));

    if (tableRows.length === 0) {
      return null;
    }
    return tableRows;
  }

  orderInformationRows: {
    sections: string[];
    propagationCompany: string;
    deliverydate: string;
    breed: string;
    gender: string;
    numberordered: number;
    numberavailable: number;
    isFulFilled: boolean;
  }[] = [];

  orderInformationTable() {
    const tableRows: {
      sections: string[];
      propagationCompany: string;
      deliverydate: string;
      breed: string;
      gender: string;
      numberordered: number;
      numberavailable: number;
      isFulFilled: boolean;
    }[] = [];

    this.selectedCycle.orders.forEach((order) => {
      for (let i = 0; i < 2; i++) {
        let gender = '';
        if (i === 0 && order.numberOfMales > 0) {
          gender = Gender.Male;
        } else if (i === 1 && order.numberOfFemales > 0) {
          gender = Gender.Female;
        }
        if (gender === Gender.Male || gender === Gender.Female) {
          tableRows.push({
            sections: [],
            propagationCompany: order.propagationCompany.name,
            deliverydate: order.arrivalDate,
            breed: order.breed.name,
            gender,
            numberordered: gender === Gender.Male ? order.numberOfMales : order.numberOfFemales,
            numberavailable: CycleUtils.getNumberOfAnimalsPerPropagationCompanyAndBreedAndGender(
              this.selectedCycle,
              order,
              gender,
              this.observations,
            ),
            isFulFilled: order.isFulFilled,
          });
        }
      }
    });

    this.selectedCycle.arrivals.forEach((arrival) => {
      arrival.allocations.forEach((allocation) => {
        const index = tableRows.findIndex(
          (row) =>
            row.breed === arrival.breed.name &&
            row.gender === arrival.gender &&
            row.propagationCompany === allocation.propagationCompany.name,
        );

        if (index !== -1) {
          if (tableRows[index].sections.indexOf(allocation.section.name) === -1) {
            tableRows[index].sections.push(allocation.section.name);
          }
        }
        tableRows[index]?.sections.sort();
      });
    });

    this.selectedCycle.movements.forEach((movement) => {
      const index = tableRows.findIndex(
        (row) =>
          row.propagationCompany === movement.toPropagationCompany.name &&
          row.breed === movement.toSection.breed.name &&
          row.gender === movement.toSection.gender,
      );

      if (index !== -1) {
        if (tableRows[index].sections.indexOf(movement.toSection.name) === -1) {
          tableRows[index].sections.push(movement.toSection.name);
        }
      }

      tableRows[index]?.sections.sort();
    });

    if (tableRows.length === 0) {
      return null;
    }
    return tableRows;
  }
}
